export const WORLDS = [
  {
    id: 'books',
    title: 'Books & Ideas',
    category: 'Literature & Speculative Thought',
    tagline: 'Chronicles from the boundary of human and artificial intellect.',
    accent: '#22D3EE',
    icon: 'BookOpen',
    detail: 'Long-form speculative work: essays, novels and field notes written at the edge where biology meets machine cognition. Every title is cross-indexed in the Library.',
    signals: ['3 titles in the vault', '2 manuscripts in progress', 'Monthly idea dispatches'],
  },
  {
    id: 'apps',
    title: 'Apps & Technology',
    category: 'Software & Studio Tools',
    tagline: 'Generative OS engines, audio synths, and spatial interfaces.',
    accent: '#10B981',
    icon: 'Cpu',
    detail: 'Instruments for thought. The studio ships experimental software — operating metaphors, synthesizers and cognition tools — built as living organisms rather than static products.',
    signals: ['4 active builds', 'Sidetrack OS v0.9', 'Open beta programme'],
  },
  {
    id: 'kyles_mind',
    title: "Kyle's Mind",
    category: 'Raw Thought Streams & Audio R&D',
    tagline: 'Unfiltered synthesis of design notes, soundscapes, and active ideas.',
    accent: '#E11D48',
    icon: 'Brain',
    detail: 'The live nervous system of the studio. Raw notes, ambient audio experiments and half-formed hypotheses are published here before they become books, apps or papers.',
    signals: ['Continuous thought stream', 'Ambient synth sessions', 'Open research log'],
  },
  {
    id: 'human',
    title: 'Human / Psychology',
    category: 'Cognition & Consciousness',
    tagline: 'Probing perception, emotion, and synthetic empathy.',
    accent: '#F59E0B',
    icon: 'UserCheck',
    detail: 'Research into what remains distinctly human: attention, memory, emotion — and how synthetic systems can meet us there with empathy rather than imitation.',
    signals: ['Neural empathy studies', 'Perception experiments', 'Ethics working notes'],
  },
  {
    id: 'cosmos',
    title: 'Cosmos / Research',
    category: 'Deep Space & Bio-Physics',
    tagline: 'Cellular mechanics in the void and quantum biological papers.',
    accent: '#8B5CF6',
    icon: 'Orbit',
    detail: 'Formal research output: papers on bio-cosmology, quantum cellular behaviour and the physics of living systems in extreme environments.',
    signals: ['Peer-reviewed papers', 'Open datasets', 'Citation-tracked'],
  },
  {
    id: 'library',
    title: 'The Library',
    category: 'Central Knowledge Repository',
    tagline: 'The cross-linked index tying all studio artifacts together.',
    accent: '#06B6D4',
    icon: 'Library',
    detail: 'Every book, app, paper and thought thread is indexed and cross-linked here. The Library is the connective tissue — the organism’s memory.',
    signals: ['Unified index', 'Cross-world links', 'Always growing'],
  },
];

export const APPS = [
  {
    id: 'sidetrack-os',
    name: 'Sidetrack OS',
    status: 'BETA v0.9',
    tagline: 'A spatial canvas for non-linear thinking.',
    description: 'An operating metaphor where ideas grow as organisms — notes branch, merge and bioluminesce as connections form between them.',
    specs: ['Spatial graph canvas', 'Offline-first sync', 'Ambient focus modes'],
    stack: ['React', 'WebGL', 'CRDT'],
    accent: '#10B981',
  },
  {
    id: 'synapse-synth',
    name: 'Synapse Synth',
    status: 'LAB BUILD',
    tagline: 'A generative audio organism.',
    description: 'A browser-native synth that evolves patches like cultures in a petri dish — mutation, selection and drift applied to sound design.',
    specs: ['Generative sequencing', 'Neural preset breeding', 'MIDI + MPE'],
    stack: ['Web Audio API', 'TensorFlow.js', 'WASM'],
    accent: '#22D3EE',
  },
  {
    id: 'aura-cognition',
    name: 'Aura Cognition',
    status: 'CONCEPT',
    tagline: 'A wearable companion for attention.',
    description: 'Research prototype mapping cognitive load into ambient light fields — making the invisible weather of the mind visible and gentle.',
    specs: ['EEG signal mapping', 'Ambient light fields', 'Privacy on-device'],
    stack: ['Edge ML', 'BLE', 'Rust'],
    accent: '#F59E0B',
  },
  {
    id: 'cosmoscript-ide',
    name: 'CosmoScript IDE',
    status: 'ALPHA',
    tagline: 'An editor that treats code as a living system.',
    description: 'An experimental IDE where codebases are rendered as constellations — dependencies orbit, dead code dims, hot paths glow.',
    specs: ['Constellation code maps', 'Live runtime telemetry', 'Semantic zoom'],
    stack: ['TypeScript', 'LSP', 'Canvas 2D'],
    accent: '#8B5CF6',
  },
];

export const BOOKS = [
  {
    id: 'synthetic-minds',
    title: 'The Biology of Synthetic Minds',
    year: '2026',
    status: 'FEATURED',
    accent: '#22D3EE',
    blurb: 'What if intelligence is not computed but grown? A field guide to minds that metabolise information the way cells metabolise light.',
    excerpt: 'A mind, like a reef, is not built. It accretes. Layer upon layer of small certainties, until one day the structure casts a shadow large enough to think within.',
  },
  {
    id: 'cosmic-edge',
    title: 'Notes from the Cosmic Edge',
    year: '2025',
    status: 'IN THE VAULT',
    accent: '#8B5CF6',
    blurb: 'Dispatches from the borderlands of physics and biology — where the vacuum of space turns out to be less empty than we feared.',
    excerpt: 'The void is not silent. It hums at the frequency of everything that almost happened.',
  },
  {
    id: 'algorithmic-poetics',
    title: 'Algorithmic Poetics',
    year: '2025',
    status: 'IN THE VAULT',
    accent: '#E11D48',
    blurb: 'A meditation on machine-authored verse, and what it reveals about the human hunger for pattern, rhythm and surprise.',
    excerpt: 'The algorithm does not dream of electric sheep. It dreams of rhymes we were too afraid to finish.',
  },
];

export const PAPERS = [
  {
    id: 'bio-cosmology',
    title: 'Toward a Bio-Cosmology: Living Systems as Models of the Early Universe',
    journal: 'Sidetrack Research Papers',
    year: '2026',
    citations: 41,
    tags: ['Bio-cosmology', 'Systems theory'],
    abstract: 'We propose that self-organising biological systems offer a tractable analogue for studying inflationary dynamics, and derive a shared mathematical vocabulary between morphogenesis and cosmic structure formation.',
  },
  {
    id: 'neural-empathy',
    title: 'Synthetic Empathy: Bounds on Machine Models of Human Affect',
    journal: 'Human / Psychology Series',
    year: '2026',
    citations: 27,
    tags: ['Neural empathy', 'Affective computing'],
    abstract: 'An empirical study of where synthetic models of emotion succeed, where they fail, and why the failures are more instructive than the successes.',
  },
  {
    id: 'quantum-cell',
    title: 'Quantum Coherence in Cellular Microtubules: A Replication Protocol',
    journal: 'Cosmos / Research Series',
    year: '2025',
    citations: 63,
    tags: ['Quantum biology', 'Open methods'],
    abstract: 'A fully open replication protocol for testing quantum coherence claims in cytoskeletal structures, with negative-result reporting built into the design.',
  },
];

export const THOUGHT_STREAM = [
  { id: 1, type: 'NOTE', time: 'T-minus 2h', accent: '#E11D48', text: 'What if the Library card UI behaves like cell division — each node splitting into related worlds on hover?' },
  { id: 2, type: 'AUDIO', time: 'T-minus 9h', accent: '#22D3EE', text: 'Ambient session 014: slow drone in D, two detuned oscillators, filtered through a model of deep-sea pressure.' },
  { id: 3, type: 'EXPERIMENT', time: 'T-minus 1d', accent: '#8B5CF6', text: 'Fed the manifesto chapters into a growth simulation. Chapter 03 keeps mutating into poetry. Keeping it.' },
  { id: 4, type: 'NOTE', time: 'T-minus 2d', accent: '#F59E0B', text: 'Human world needs a piece on attention as a tide, not a spotlight. Outline drafted in Sidetrack OS.' },
  { id: 5, type: 'BUILD', time: 'T-minus 3d', accent: '#10B981', text: 'CosmoScript constellation renderer now dims dead code like dying stars. Shipping in alpha 0.3.' },
];

export const PROJECTS = [
  { id: 'os-drop', name: 'Sidetrack OS — Public Beta', progress: 82, accent: '#10B981', eta: 'Q3 2026' },
  { id: 'book-drop', name: 'The Biology of Synthetic Minds — First Print', progress: 64, accent: '#22D3EE', eta: 'Q4 2026' },
  { id: 'synth-drop', name: 'Synapse Synth — Lab Sessions Vol. 1', progress: 45, accent: '#E11D48', eta: 'Q4 2026' },
];

export const MANIFESTO = [
  {
    num: '01',
    title: 'The Living Void',
    text: 'We treat emptiness as a medium, not an absence. Space — cosmic or cognitive — is where structures grow. The studio begins in the dark, on purpose.',
  },
  {
    num: '02',
    title: 'Synthetic Symbiosis',
    text: 'Machines are not our replacements or our mirrors. They are a new kingdom of life we are learning to grow alongside — carefully, curiously, without illusions.',
  },
  {
    num: '03',
    title: 'Thought Beyond Geometry',
    text: 'Ideas do not move in straight lines. They branch, drift, mutate and merge. Our tools, books and research are built for thought as it actually behaves.',
  },
  {
    num: '04',
    title: 'The Open Infinite',
    text: 'Everything the studio learns is published into the commons — papers, protocols, failed experiments. The infinite stays open or it is not infinite at all.',
  },
];

export const IMAGES = {
  hero: 'https://images.unsplash.com/photo-1707588884096-0c2e19cc3aed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHxiaW9sdW1pbmVzY2VudCUyMG9yZ2FuaXNtJTIwZGVlcCUyMHNwYWNlJTIwbmVidWxhJTIwYmlvbHVtaW5lc2NlbnQlMjBjb3NtaWMlMjB0ZXh0dXJlfGVufDB8fHx8MTc4OTQzNDM4NXww&ixlib=rb-4.1.0&q=85',
  synapse: 'https://images.unsplash.com/photo-1719650592946-55163c4994cb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxzeW5hcHNlJTIwYnJhaW4lMjBuZXR3b3JrJTIwbmV1cmFsJTIwYmlvbHVtaW5lc2NlbnQlMjBkYXJrfGVufDB8fHx8MTc4OTQzNDM4NXww&ixlib=rb-4.1.0&q=85',
  books: 'https://images.unsplash.com/photo-1532012197267-da84d127e765?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxjb3NtaWMlMjBnbG93aW5nJTIwYm9vayUyMGRhcmslMjBsaWJyYXJ5JTIwYmlvbHVtaW5lc2NlbnR8ZW58MHx8fHwxNzg5NDM0Mzg1fDA&ixlib=rb-4.1.0&q=85',
  hud: 'https://images.unsplash.com/photo-1720962158937-7ea890052166?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwyfHxmdXR1cmlzdGljJTIwaG9sb2dyYXBoaWMlMjBpbnRlcmZhY2UlMjB0ZWNobm9sb2d5JTIwZGFyayUyMGN5YmVyfGVufDB8fHx8MTc4OTQzNDM4NXww&ixlib=rb-4.1.0&q=85',
};
